import { GTMEventNameEcommerce, sendGTMEventEcommerce } from '@/fo/api/gtm';
import { CartItemInput, cartService, CartItemFragment } from '@/fo/api/magento';
import { cartMindboxService } from '@/fo/api/mindbox';
import { toastBase } from '@/fo/ui/kit/toast/base';
import { toastCartAdded } from '@/fo/ui/toast/added';
import { MappedProduct } from '@/fo/util/map-product-data';

import { selectCountProductInCart, storeProduct } from '../lib/store';

type FieldProduct =
  | 'sku'
  | 'parentSku'
  | 'name'
  | 'brand'
  | 'series'
  | 'volume'
  | 'basePrice'
  | 'finalPrice'
  | 'itemPrice'
  | 'baseImage'
  | 'lastLevelCategory';

type InputProduct = Pick<MappedProduct, FieldProduct>;

type InputItemAdd = CartItemInput & {
  product: InputProduct & {
    link?: string;
  };
};
declare global {
  interface Window {
    upravel?: (
      id: string,
      goal: string,
      action: string,
      parameters: string,
      cnl: number
    ) => void;
  }
}

const getCategoryItem = (item: InputProduct): string => {
  const category =
    (item.lastLevelCategory?.breadcrumbs && item.lastLevelCategory?.name) ??
    item.brand ??
    '';

  return category;
};

const getPriceItem = (item: InputProduct) => {
  return item.itemPrice ?? item.finalPrice ?? item.basePrice ?? 0;
};

const addProductsToCart = async (cartItems: InputItemAdd[]) => {
  cartItems.forEach((item) => {
    storeProduct.send({
      type: 'setProductCartLoading',
      sku: item.sku,
      loading: true
    });
  });

  const response = await cartService.addProductsToCart(
    cartItems.map((item) => ({
      sku: item.sku,
      quantity: item.quantity
    }))
  );

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  if (!response?.cart || !response?.items) {
    return;
  }

  response.items.forEach((item) => {
    if (item?.product && item?.product.sku) {
      storeProduct.send({
        type: 'setProduct',
        sku: item?.product.sku,
        cart: {
          id: item?.id ?? '',
          count: item?.quantity ?? 1,
          loading: false,
          isGift: (item as CartItemFragment)?.is_cart_rule_gift ?? false
        }
      });
    }
  });

  cartItems.forEach((item) => {
    if (item.sku === item?.product.sku) {
      toastCartAdded({
        src: item.product.baseImage?.src || '',
        subtitle: item?.product.name || '',
        title: response.toast.title,
        buttonText: response.toast.buttonText,
        style: { top: '0' }
      });
      window.upravel?.(
        '29300000561',
        'any',
        'basket',
        `client_id_null_url_${item?.product.link}`,
        1
      );
    }
  });
  sendGTMEventEcommerce({
    event: 'add',
    ecommerce: {
      products: cartItems.map((item) => ({
        id: item.sku,
        name: item.product.name || '',
        price: getPriceItem(item.product),
        brand: item.product.brand || '',
        category: getCategoryItem(item.product),
        quantity: item.quantity
      }))
    }
  });

  const mindboxItems =
    response.cart?.itemsV2?.items?.map((item) => ({
      id: item?.product.ean || '',
      count: item?.quantity || 0,
      price: item?.prices?.price?.value || 0
    })) || [];

  await cartMindboxService.update(mindboxItems);
};

const updateCartItem = async (
  sku: string,
  cartItemId: string,
  quantity: number,
  product: InputProduct
) => {
  storeProduct.send({
    type: 'setProductCartLoading',
    sku,
    loading: true
  });
  const response = await cartService.updateCartItem(cartItemId, quantity);

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  if (!response?.cart) {
    return;
  }

  const snapshot = storeProduct.getSnapshot();
  const prevCount = selectCountProductInCart(sku)(snapshot);
  const eventGTM: GTMEventNameEcommerce =
    prevCount > quantity ? 'remove' : 'add';

  const priceItem = getPriceItem(product);
  const quantityItem = Math.abs(quantity - prevCount);

  sendGTMEventEcommerce({
    event: eventGTM,
    ecommerce: {
      products: [
        {
          id: sku,
          name: product.name || '',
          price: priceItem,
          brand: product.brand || '',
          category: getCategoryItem(product),
          quantity: quantityItem
        }
      ]
    }
  });

  const mindboxItems =
    response.cart?.itemsV2?.items?.map((item) => ({
      id: item?.product.ean || '',
      count: item?.quantity || 0,
      price: item?.prices?.price?.value || 0
    })) || [];

  await cartMindboxService.update(mindboxItems);
};

const removeCartItem = async (
  sku: string,
  cartItemId: string,
  product: InputProduct
) => {
  storeProduct.send({
    type: 'setProductCartLoading',
    sku,
    loading: true
  });
  const response = await cartService.removeCartItem(cartItemId);

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  const snapshot = storeProduct.getSnapshot();
  const prevCount = selectCountProductInCart(sku)(snapshot);

  const priceItem = getPriceItem(product);
  sendGTMEventEcommerce({
    event: 'remove',
    ecommerce: {
      products: [
        {
          id: sku,
          name: product.name || '',
          price: priceItem,
          brand: product.brand || '',
          category: getCategoryItem(product),
          quantity: prevCount
        }
      ]
    }
  });

  const mindboxItems =
    response.cart?.items?.map((item) => ({
      id: item?.product?.ean || '',
      count: item?.quantity || 0,
      price: item?.prices?.price?.value || 0
    })) || [];

  await cartMindboxService.update(mindboxItems);
};

type InputItemRemoveAll = {
  id: string;
  product: InputProduct;
};

const removeAllCartItem = async (cartItems: InputItemRemoveAll[] = []) => {
  const response = await cartService.removeAllCartItem(
    cartItems.map((item) => item.id)
  );

  if (response?.errorMessage) {
    toastBase(response.errorMessage, { status: 'error' });
  }

  const snapshot = storeProduct.getSnapshot();

  storeProduct.send({ type: 'setClearedCart' });

  sendGTMEventEcommerce({
    event: 'remove',
    ecommerce: {
      products: cartItems.map((item) => ({
        id: item.product.sku,
        name: item.product.name || '',
        price: getPriceItem(item.product),
        brand: item.product.brand || '',
        category: getCategoryItem(item.product),
        quantity: selectCountProductInCart(item.product.sku)(snapshot)
      }))
    }
  });
};

export const cartActions = {
  addProductsToCart,
  updateCartItem,
  removeCartItem,
  removeAllCartItem
};
